import { useForm } from "react-hook-form";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Text from "src/components/Displays/Text";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { SMART_CHARGING_ROUTES } from "src/consts/routes";
import BackButton from "src/components/Shared/BackButton";
import { ThemeValues } from "src/components/ThemeUsage/theme";
import { ThemeContext } from "src/components/ThemeUsage/ThemeUsage";
import FormSubmitContainer from "src/components/Shared/FormSubmitContainer";
import ConnectConfirmForm from "src/components/Forms/SmartCharging/ConnectConfirmForm";

export interface ConnectConfirmFormValues {
  wallboxConfigured: boolean;
  wallboxRestarted: boolean;
}

const ConnectConfirm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const themeName = useContext(ThemeContext);

  const [formSubmitDisabled, setFormSubmitDisabled] = useState(true);

  const form = useForm<ConnectConfirmFormValues>({
    defaultValues: {
      wallboxConfigured: false,
      wallboxRestarted: false,
    },
  });

  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (name === "wallboxConfigured" || name === "wallboxRestarted") {
        if (
          form.getValues().wallboxConfigured === true &&
          form.getValues().wallboxRestarted === true
        ) {
          setFormSubmitDisabled(false);
        } else {
          setFormSubmitDisabled(true);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = async (data: ConnectConfirmFormValues) => {
    navigate(SMART_CHARGING_ROUTES.CONNECTING);
  };

  return (
    <>
      <Stack gap={5}>
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <BackButton
            variant={
              themeName === ThemeValues.lichtblick ||
              themeName === ThemeValues.SMA
                ? "dark"
                : "light"
            }
            onClick={() => navigate(-1)}
            sx={(theme) => ({
              backgroundColor: theme.palette.background.default,
              "&:hover": {
                backgroundColor: theme.palette.background.default,
              },
              display: { xs: "none", sm: "flex" },
            })}
          />
          <Text
            text={t("page.wallboxConnectConfirm.pageTitle")}
            sx={(theme) => ({
              fontSize: "32px",
              lineHeight: "35px",
              fontFamily: "isonType",
              color: theme.palette.mainFont.primary,
            })}
          />
        </Stack>

        <Text
          text={t("page.wallboxConnectConfirm.pageSubTitle1")}
          sx={(theme) => ({
            fontSize: "14px",
            lineHeight: "24px",
            color: theme.palette.mainFont.primary,
          })}
        />

        <Box sx={{ maxWidth: { xs: "100%", sm: "380px" } }}>
          <Box
            component={"form"}
            autoComplete="off"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <Box sx={{ marginTop: 6, marginBottom: 3 }}>
              <ConnectConfirmForm form={form} />
            </Box>

            <FormSubmitContainer
              disabled={formSubmitDisabled}
              submitButtonText={t("page.wallboxConnectConfirm.nextButton")}
              sx={(theme) => ({
                fontSize: "14px",
                lineHeight: "20px",
                color: theme.palette.mainFont.primary,

                "&:disabled": {
                  opacity: 0.1,
                  bgcolor: "#000",
                  fontSize: "14px",
                  lineHeight: "20px",
                  cursor: "not-allowed",
                  pointerEvents: "all !important",
                },
              })}
            />
          </Box>
        </Box>
      </Stack>
    </>
  );
};

export default ConnectConfirm;
